'use client';

import React from 'react';
import { FormControl, InputLabel, Select, SelectProps, FormHelperText, MenuItem } from '@mui/material';
import { FormLabel } from './FormLabel';

export interface FormSelectProps extends Omit<SelectProps, 'label'> {
  /** The label text to display */
  label?: string;
  /** Whether this field is required (if false/undefined, field is optional) */
  required?: boolean;
  /** Whether the field is in edit mode (required indicator only shows in edit mode) */
  isEditMode?: boolean;
  /** Error state */
  error?: boolean;
  /** Helper text */
  helperText?: React.ReactNode;
  /** Size of the input */
  size?: 'small' | 'medium';
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Options for the select */
  options?: Array<{ value: string | number; label: string }>;
  /** Full width */
  fullWidth?: boolean;
}

export const FormSelect: React.FC<FormSelectProps> = ({
  label,
  required,
  isEditMode = true,
  error,
  helperText,
  size = 'medium',
  disabled,
  options = [],
  fullWidth = true,
  ...selectProps
}) => {
  const formLabel = label ? (
    <FormLabel required={required} optional={!required} isEditMode={isEditMode}>
      {label}
    </FormLabel>
  ) : undefined;

  return (
    <FormControl fullWidth={fullWidth} error={error} size={size} disabled={disabled}>
      <InputLabel>{formLabel}</InputLabel>
      <Select {...selectProps} label={formLabel} size={size} disabled={disabled} required={required}>
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
    </FormControl>
  );
};

export default FormSelect;
